import type { TFunction } from "i18next"

import {
  WHY_BAND_THRESHOLDS,
  WHY_NORTH_STAR_KPI_NAME,
  windowSpanDays,
  type DrawerWhyKpiFormat,
  type DrawerWhyKpiOption,
  type DrawerWhyTagKpiStats,
  type DrawerWhyTagRow,
  type TopicExampleRow,
} from "@nexus/contracts"
import { compileKpiFormula, evalKpi, KpiFormulaError } from "@nexus/kpi-formula"

import { formatMetricValue } from "@/features/dashboard/lib/format"

import type { MyBrandCreativeCardModel } from "./drilldown-card.types"
import { daysBetweenYmd } from "./drilldown-row"
import { formatTableValue } from "./format-table-value"

/** Axis half-width: the range bar reads −40%…+40% around the baseline, clamped beyond. */
export const WHY_AXIS_MAX = 0.4

/** One tag read in the KPI the tab shows: the row plus that KPI's stats. */
export type WhyTagView = Omit<DrawerWhyTagRow, "kpis"> & DrawerWhyTagKpiStats

const NO_STATS: DrawerWhyTagKpiStats = {
  value: null,
  p25: null,
  p75: null,
  delta: null,
  p25Delta: null,
  p75Delta: null,
  band: "low",
}

/** The rows read in one KPI, best first for its direction (the insight and bands rely on it). */
export function whyRowsForKpi(rows: DrawerWhyTagRow[], option: DrawerWhyKpiOption): WhyTagView[] {
  const views = rows.map(({ kpis, ...row }) => ({ ...row, ...(kpis[option.key] ?? NO_STATS) }))
  const goodness = (view: WhyTagView) => whyGoodness(view.delta, option.lowerIsBetter) ?? 0
  return views.sort((a, b) => goodness(b) - goodness(a))
}

/** Browser key of the tab's KPI choice, per tenant (KPI names differ between tenants). */
const WHY_KPI_STORAGE_PREFIX = "nexus.drilldown.whyKpi."

export function readStoredWhyKpi(tenantId: string | null): string | null {
  if (!tenantId) return null
  try {
    return window.localStorage.getItem(WHY_KPI_STORAGE_PREFIX + tenantId)
  } catch {
    return null
  }
}

export function storeWhyKpi(tenantId: string | null, key: string): void {
  if (!tenantId) return
  try {
    window.localStorage.setItem(WHY_KPI_STORAGE_PREFIX + tenantId, key)
  } catch {
    // Private mode or blocked storage: the choice lasts for this page only.
  }
}

/**
 * The KPI the tab shows, from the options with data: the person's last choice, else the page
 * North Star (`roas` / `cpa` / `custom`), else the first option. Null when no KPI has data.
 */
export function resolveWhyKpi(
  options: DrawerWhyKpiOption[],
  chosen: string | null,
  northStar: string,
): DrawerWhyKpiOption | null {
  const byKey = (key: string | null | undefined) => (key ? options.find((o) => o.key === key) : undefined)
  const northStarKey = WHY_NORTH_STAR_KPI_NAME[northStar as keyof typeof WHY_NORTH_STAR_KPI_NAME]
  return byKey(chosen) ?? byKey(northStarKey) ?? options[0] ?? null
}

/** An example creative's own value in the KPI: the option's formula on the row's raw columns. */
export function exampleKpiValue(row: TopicExampleRow, option: DrawerWhyKpiOption): number | null {
  if (!row.columns) return null
  try {
    return evalKpi(compileKpiFormula(option.formula), row.columns, {})
  } catch (err) {
    if (err instanceof KpiFormulaError) return null
    throw err
  }
}

/** Stable key for a tag row (same grain as the SQL). */
export function whyTagKey(row: Pick<DrawerWhyTagRow, "scenario" | "subFeature" | "topicTitle">): string {
  return `${row.scenario}|${row.subFeature}|${row.topicTitle}`
}

/** Positive = better for the KPI, whatever its polarity. */
function whyGoodness(delta: number | null, lowerIsBetter: boolean): number | null {
  if (delta === null) return null
  return lowerIsBetter ? -delta : delta
}

/** Left offset (0–100) on the axis; better sits right of the baseline for every KPI. */
export function whyAxisPercent(delta: number | null, lowerIsBetter: boolean): number | null {
  const goodness = whyGoodness(delta, lowerIsBetter)
  if (goodness === null) return null
  const clamped = Math.max(-WHY_AXIS_MAX, Math.min(WHY_AXIS_MAX, goodness))
  return 50 + (clamped / WHY_AXIS_MAX) * 50
}

const normalizeName = (value: string) => value.trim().toLowerCase().replace(/_/g, " ")

/**
 * Acronyms kept uppercase in tag values ("ugc" → "UGC").
 * `us` is not in the list: a word match turned "trust us" into "trust US".
 * "USA" still matches `usa`.
 */
const UPPERCASE_WORDS = /\b(ugc|cta|ai|uk|usa|tv|3d)\b/gi

/**
 * Display value of a tag. Drops a leading "<sub-feature>;" or "<sub-feature>:" when the prefix
 * is the row's own sub-feature ("presenter type;founder" → "Founder", "word_count:10-15" →
 * "10-15"), capitalises the first letter and keeps known acronyms uppercase. The raw
 * Topic_Title stays in the data.
 */
export function whyTagLabel(row: Pick<DrawerWhyTagRow, "topicTitle" | "subFeature">): string {
  const title = row.topicTitle.trim()
  const cut = title.search(/[;:]/)
  const value =
    cut > 0 && normalizeName(title.slice(0, cut)) === normalizeName(row.subFeature)
      ? title.slice(cut + 1).trim()
      : title
  const cased = value.charAt(0).toUpperCase() + value.slice(1)
  return cased.replace(UPPERCASE_WORDS, (word) => word.toUpperCase())
}

/** Plain-words name of each Scenario / Sub_Feature pair the tab reads. */
const SUB_FEATURE_LABELS: Record<string, readonly [key: string, defaultValue: string]> = {
  "Concept|Main": ["drilldown.myBrandDrawer.why.subFeature.mainConcept", "Main concept"],
  "Concept|Primary": ["drilldown.myBrandDrawer.why.subFeature.primaryConcept", "Primary concept"],
  "Narrative|Painpoint": ["drilldown.myBrandDrawer.why.subFeature.painPoint", "Pain point"],
  "Narrative|Motivation": ["drilldown.myBrandDrawer.why.subFeature.motivation", "Motivation"],
  "Voice Over|Automatic": ["drilldown.myBrandDrawer.why.subFeature.voiceOver", "Voice over"],
  "Text Overlay|Automatic": ["drilldown.myBrandDrawer.why.subFeature.textOverlay", "Text overlay"],
  "Presenter|Age": ["drilldown.myBrandDrawer.why.subFeature.age", "Age"],
  "Presenter|Presenter Type": ["drilldown.myBrandDrawer.why.subFeature.presenterType", "Presenter type"],
  "Presenter|Gender": ["drilldown.myBrandDrawer.why.subFeature.gender", "Gender"],
  "Background|Production Style": ["drilldown.myBrandDrawer.why.subFeature.productionStyle", "Production style"],
  "Background|Filming Location": ["drilldown.myBrandDrawer.why.subFeature.filmingLocation", "Filming location"],
  "Asset Metadata|Video Length": ["drilldown.myBrandDrawer.why.subFeature.format", "Format"],
  "Sequence|Sequence Order 1": ["drilldown.myBrandDrawer.why.subFeature.opensWith", "Opens with"],
}

/** Row subtitle name: "Production style", "Main concept", "Opens with". */
export function whySubFeatureLabel(
  row: Pick<DrawerWhyTagRow, "scenario" | "subFeature">,
  t: TFunction,
): string {
  const known = SUB_FEATURE_LABELS[`${row.scenario}|${row.subFeature}`]
  if (known) return t(known[0], known[1])
  const sub = normalizeName(row.subFeature)
  const value = sub.charAt(0).toUpperCase() + sub.slice(1)
  return t("drilldown.myBrandDrawer.why.subFeature.fallback", "{{value}}", { value })
}

/**
 * Sub-features whose values are generic on their own ("Both", "20s", "UGC"): the insight
 * always names them as "Sub-feature: value".
 */
const PREFIXED_IN_INSIGHT = new Set([
  "Presenter|Gender",
  "Presenter|Age",
  "Asset Metadata|Video Length",
  "Background|Production Style",
  "Background|Filming Location",
  "Presenter|Presenter Type",
  "Sequence|Sequence Order 1",
])

/**
 * Names of the insight's tags, in order. "Sub-feature: value" ("Gender: Both") for the generic
 * sub-features, and for any tag whose label another named tag shares; the bare label otherwise.
 */
export function whyInsightNames(named: WhyTagView[], t: TFunction): string[] {
  const labels = named.map((row) => whyTagLabel(row))
  const counts = new Map<string, number>()
  for (const label of labels) counts.set(label.toLowerCase(), (counts.get(label.toLowerCase()) ?? 0) + 1)
  return named.map((row, i) => {
    const label = labels[i]!
    const prefixed =
      PREFIXED_IN_INSIGHT.has(`${row.scenario}|${row.subFeature}`) || (counts.get(label.toLowerCase()) ?? 0) > 1
    return prefixed
      ? t("drilldown.myBrandDrawer.why.namedTag", "{{subFeature}}: {{value}}", {
          subFeature: whySubFeatureLabel(row, t),
          value: label,
        })
      : label
  })
}

/** KPI value in the KPI's format; a ratio (ROAS) with two decimals under 10, one from 10 up. */
export function formatWhyKpiValue(value: number | null, format: DrawerWhyKpiFormat, currencySymbol: string): string {
  if (format !== "ratio") return formatTableValue(value, format, currencySymbol)
  if (value === null || !Number.isFinite(value)) return "—"
  return formatMetricValue(value, "ratio", currencySymbol, Math.abs(value) < 10 ? 2 : 1)
}

/** Signed whole percent with a true minus sign: −24%, +14%. */
export function formatWhyDelta(delta: number | null): string {
  if (delta === null || !Number.isFinite(delta)) return "—"
  const pct = Math.round(delta * 100)
  if (pct === 0) return "0%"
  return pct > 0 ? `+${pct}%` : `−${Math.abs(pct)}%`
}

function joinNames(names: string[], t: TFunction): string {
  if (names.length <= 1) return names[0] ?? ""
  return t("drilldown.myBrandDrawer.why.nameList", "{{list}} and {{last}}", {
    list: names.slice(0, -1).join(", "),
    last: names[names.length - 1] ?? "",
  })
}

export interface WhyInsight {
  text: string
  /** Tags named in the text, in order (chips). */
  named: WhyTagView[]
  /** Display name of each named tag, same order (see whyInsightNames). */
  names: string[]
}

/**
 * Templated insight (no LLM): the top two helping tags and the top hurting tag.
 * Low-sample tags are never named. `rows` come best first (whyRowsForKpi); `kpiLabel` and
 * `lowerIsBetter` are the shown KPI's.
 */
export function buildWhyInsight(
  rows: WhyTagView[],
  kpiLabel: string,
  lowerIsBetter: boolean,
  t: TFunction,
): WhyInsight {
  const helping = rows.filter((r) => r.band === "helping" && !r.lowSample).slice(0, 2)
  const hurting = rows
    .filter((r) => r.band === "hurting" && !r.lowSample)
    .slice(-1)
    .reverse()
  const direction = t(
    lowerIsBetter ? "drilldown.myBrandDrawer.why.directionUnder" : "drilldown.myBrandDrawer.why.directionAbove",
    lowerIsBetter ? "under" : "above",
  )
  const percent = Math.round(WHY_BAND_THRESHOLDS.LOW_IMPACT_ABS_DELTA * 100)
  const named = [...helping, ...hurting]
  const names = whyInsightNames(named, t)
  const helpingNames = names.slice(0, helping.length)
  const hurtingName = names[helping.length]

  const sentences: string[] = []
  if (helping.length === 2) {
    sentences.push(
      t(
        "drilldown.myBrandDrawer.why.insightHelpingTwo",
        "{{names}} are doing most of the work: your creatives with each one run {{direction}} your {{kpi}} baseline.",
        { names: joinNames(helpingNames, t), direction, kpi: kpiLabel },
      ),
    )
  } else if (helping.length === 1) {
    sentences.push(
      t(
        "drilldown.myBrandDrawer.why.insightHelpingOne",
        "{{name}} is doing most of the work: your creatives with it run {{direction}} your {{kpi}} baseline.",
        { name: helpingNames[0], direction, kpi: kpiLabel },
      ),
    )
  } else {
    sentences.push(
      t(
        "drilldown.myBrandDrawer.why.insightNoHelping",
        "No tag in this creative beats your {{kpi}} baseline by more than {{percent}}% on its own.",
        { kpi: kpiLabel, percent },
      ),
    )
  }
  if (hurtingName !== undefined) {
    sentences.push(
      helping.length > 0
        ? t("drilldown.myBrandDrawer.why.insightHurtingWithHelp", "{{name}} is pulling the other way.", {
            name: hurtingName,
          })
        : t("drilldown.myBrandDrawer.why.insightHurtingOnly", "{{name}} weighs on it most.", {
            name: hurtingName,
          }),
    )
  }

  return { text: sentences.join(" "), named, names }
}

function todayYmd(now: Date): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, "0")
  const d = String(now.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function formatUtcDate(date: Date, locale: string, withYear: boolean): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" as const } : {}),
    timeZone: "UTC",
  }).format(date)
}

/** "last 90 days" when the window ends today, else a locale-formatted range. */
export function formatWhyWindow(
  startDate: string,
  endDate: string,
  t: TFunction,
  locale: string,
  now = new Date(),
): string {
  const start = new Date(`${startDate}T00:00:00Z`)
  const end = new Date(`${endDate}T00:00:00Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ""
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
  if (endDate === todayYmd(now)) {
    return t("drilldown.myBrandDrawer.why.lastDays", { count: days })
  }
  return t("drilldown.myBrandDrawer.why.dateRange", "{{start}} – {{end}}", {
    start: formatUtcDate(start, locale, false),
    end: formatUtcDate(end, locale, true),
  })
}

/** Display name of an example creative (ad name, else example label, else short hash). */
export function exampleName(row: TopicExampleRow): string {
  const isReal = (v: string | null | undefined) => !!v && v.trim() !== "" && v.trim().toLowerCase() !== "all"
  if (isReal(row.adName)) return row.adName
  if (isReal(row.example)) return row.example
  return row.hash.slice(0, 10)
}

const WHY_EXAMPLE_ID_PREFIX = "why-example:"

/** segment1 value of the hash-level grid, the only grouping whose card is a whole creative. */
const HASH_SEGMENT = "Hash"

/** The card is an example stub (the creative was not in the loaded grid). */
export function isWhyExampleStub(card: Pick<MyBrandCreativeCardModel, "id">): boolean {
  return card.id.startsWith(WHY_EXAMPLE_ID_PREFIX)
}

const YMD_RANGE = /^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})$/

/** Example `datesActive` ("2026-07-01 to 2026-09-30") → its dates; empty when it does not parse. */
function parseDatesActive(datesActive: string): { minDate: string; maxDate: string } {
  const match = YMD_RANGE.exec(datesActive.trim())
  return match ? { minDate: match[1]!, maxDate: match[2]! } : { minDate: "", maxDate: "" }
}

/**
 * Drawer card model for an example creative, so the drawer can open it in place.
 * Dates come from the example's `datesActive`. Grouping is All/All: the stub stands for the
 * whole creative, so the grouping-scoped queries read all of it. Grid-only fields (KPIs,
 * dup badge) stay empty — the drawer does not read them.
 */
export function exampleToCardModel(row: TopicExampleRow): MyBrandCreativeCardModel {
  const { minDate, maxDate } = parseDatesActive(row.datesActive ?? "")
  return {
    id: `${WHY_EXAMPLE_ID_PREFIX}${row.hash}`,
    hash: row.hash,
    isVideo: row.objectType.toLowerCase() !== "image",
    objectTypeLabel: row.objectType,
    thumbnailUrl: row.thumbnailUrl,
    assetUrl: row.creativeUrl,
    adLibUrl: row.adLibUrl,
    dupCount: null,
    adsCount: row.ads,
    daysActive: daysBetweenYmd(minDate, maxDate),
    minDate,
    maxDate,
    kpis: [],
    adId: null,
    grouping: { groupby2: "All", groupby3: "All", groupingKey: "All" },
  }
}

/**
 * The card the drawer opens for an example jump: always the whole creative (hash level), never
 * one group's slice. A grid card is used only when it is hash-level (segment1 Hash, Groupby2 and
 * Groupby3 both All, the flat grid): it carries the ad scope and KPIs the stub does not. Under
 * any other first grouping (e.g. Ad ID) or a Group By split the grid card is one slice, so the
 * jump opens the All/All stub instead.
 */
export function resolveExampleJumpCard(
  stub: MyBrandCreativeCardModel,
  gridCards: MyBrandCreativeCardModel[],
  segment1: string,
): MyBrandCreativeCardModel {
  if (segment1 !== HASH_SEGMENT) return stub
  return (
    gridCards.find(
      (card) =>
        card.hash === stub.hash && card.grouping.groupby2 === "All" && card.grouping.groupby3 === "All",
    ) ?? stub
  )
}
